/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Models */
import { DsCategory } from '../models/ds-category';

@Injectable({
  providedIn: 'root',
})
export class DsFormTemplateModelCategoriesService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get form template model categories.
   *
   * @param organisationId Organisation ID
   * @param elementsPerPage the elements number per page
   * @param pageIndex the page index
   * @returns Observable type `<BydModel<DsCategory>>`
   */
  public getFormTemplateModelCategories(
    organisationId: number,
    elementsPerPage = 1000,
    pageIndex = 0
  ): Observable<BydModel<DsCategory>> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    this.path = `/v2/organisations/${organisationId}/formCategories/`;
    return this.getModels<DsCategory>({ headers });
  }

  /**
   * Returns the selected category.
   *
   * @param organisationId Organisation id
   * @param categoryId Selected category id
   * @returns Observable<DsCategory>
   */
  public getCategory(organisationId: number, categoryId: number): Observable<DsCategory> {
    this.path = `/v2/organisations/${organisationId}/formCategories/${categoryId}`;
    return this.getModel<DsCategory>();
  }

  /**
   * Get form template model from category.
   *
   * @param  organisationId Organisation ID
   * @param  categoryId Category ID
   * @returns Observable type `<BydModel<DsCategory>>`
   */
  public getFormTemplateModelFromCategory(
    organisationId: number,
    categoryId: number
  ): Observable<BydModel<DsCategory>> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(1000, 1);
    this.path = `/v2/organisations/${organisationId}/formCategories//${categoryId}/forms`;
    return this.getModels<DsCategory>({ headers });
  }

  /**
   * Remove form template model category.
   *
   * @param  organisationId Organisation ID
   * @param  categoryId Category ID
   * @returns Observable type `<boolean>`
   */
  public deleteFormTemplateModelCategory(organisationId: number, categoryId: number): Observable<boolean> {
    this.path = `/v2/organisations/${organisationId}/formCategories//${categoryId}`;
    return this.delete<boolean>();
  }

  /**
   * Update category name.
   *
   * @param  organisationId Organisation ID
   * @param  categoryId Category ID
   * @param  newCategoryName new category name
   * @returns Observable type `<DsCategory>`
   */
  public updateFormTemplateModelCategory(
    organisationId: number,
    categoryId: number,
    newCategoryName: string
  ): Observable<DsCategory> {
    this.path = `/v2/organisations/${organisationId}/formCategories//${categoryId}`;
    return this.putModel<DsCategory>({ body: { name: newCategoryName } });
  }

  /**
   * Add new form template model form category.
   *
   * @param  organisationId Organisation ID
   * @param  categoryId Category ID
   * @param  formTemplateName Form template name
   * @param  configLines config lines
   * @param  newIconId New icon ID
   * @returns Observable type `<number>`
   */
  public addFormTemplateModelFromCategory(
    organisationId: number,
    categoryId: number,
    formTemplateName: string,
    configLines: Record<string, unknown>,
    newIconId: number
  ): Observable<number> {
    this.path = `/v2/organisations/${organisationId}/formCategories//${categoryId}/forms`;
    return this.post<number>({ body: { name: formTemplateName, fields: configLines, iconId: newIconId } });
  }

  /**
   * Add new form template model category.
   *
   * @param  organisationId Organisation ID
   * @param  newCategoryName New category name
   * @returns Observable type `<boolean>`
   */
  public addFormTemplateModelCategory(organisationId: number, newCategoryName: string): Observable<boolean> {
    this.path = `/v2/organisations/${organisationId}/formCategories/`;
    return this.post<boolean>({ body: { name: newCategoryName } });
  }

  /**
   * Create DsCategory instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsCategory` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsCategory {
    return new DsCategory(jsonObject);
  }
}
